import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import { formateNumber } from 'shared/helpers';
import { getTimeFromUTCToTimezone } from 'shared/utils/getTimeFromUTCToTimezone';

ChartJS.register(
  LinearScale,
  LineController,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  Filler,
);

const colors = [
  ['#b100ff', 'rgba(177, 0, 255, 0.18)'],
  ['#43ac6a', 'rgba(67, 172, 106, 0.12)'],
  ['#ffbc0f', 'rgba(255, 188, 15, 0.12)'],
  ['#2eb8d4', 'rgba(46, 184, 212, 0.12)'],
];
const comparisonColor = '#43AC6A';
const comparisonBackgroundColor = 'rgba(67, 172, 106, 0.3)';

const parseChartDate = value => getTimeFromUTCToTimezone(dayjs(value)) ?? dayjs(Number.NaN);

const formatDate = value => {
  const date = parseChartDate(value);
  if (!date.isValid()) return value;
  return /\s(?!00:00)\d{2}:\d{2}/.test(value) ? date.format('DD.MM HH:mm') : date.format('DD.MM');
};

const ReportChart = ({ series, withDiff, currency, isMobile = false }) => {
  const locale = useSelector(state => state.locale.locale);
  const chartData = useMemo(() => {
    const firstSeries = series.find(item => item.current?.length);
    const points = firstSeries?.current || [];
    const labels = points.map(item => formatDate(item.date));
    const dates = points.map(item => parseChartDate(item.date)).filter(date => date.isValid());
    const isSingleDay = dates.length > 0 && dates.every(date => date.isSame(dates[0], 'day'));
    const axisLabels = points.map(item => {
      const date = parseChartDate(item.date);
      if (!date.isValid()) return item.date;
      if (isSingleDay) return date.format('HH:mm');
      return date.isSame(parseChartDate(dayjs()), 'day') ? locale.today : date.format('DD.MM');
    });
    const datasets = [];

    series.forEach((item, index) => {
      const [color, backgroundColor] = colors[index % colors.length];
      datasets.push({
        label: item.label,
        data: item.current.map(point => point.value),
        dates: item.current.map(point => point.date),
        comparison: item.previous.map(point => point.value),
        borderColor: color,
        backgroundColor,
        pointBackgroundColor: color,
        pointBorderColor: color,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: true,
        tension: 0.25,
        suffix: item.suffix,
        showCurrency: item.currency,
      });

      if (withDiff && item.previous.length) {
        datasets.push({
          label: `${item.label} · ${locale.previousPeriod}`,
          data: item.previous.map(point => point.value),
          dates: item.previous.map(point => point.date),
          borderColor: comparisonColor,
          backgroundColor: comparisonBackgroundColor,
          pointBackgroundColor: comparisonColor,
          pointBorderColor: comparisonColor,
          borderWidth: 1,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: '-1',
          tension: 0.25,
          suffix: item.suffix,
          showCurrency: item.currency,
          isPrevious: true,
        });
      }
    });

    return { labels, axisLabels, datasets };
  }, [locale.previousPeriod, locale.today, series, withDiff]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        legend: {
          display: chartData.datasets.length > 1,
          labels: {
            color: '#f4eafe',
            usePointStyle: true,
            boxWidth: 12,
            boxHeight: 10,
            padding: 18,
          },
        },
        tooltip: {
          backgroundColor: '#320548',
          borderColor: '#682984',
          borderWidth: 1,
          padding: 12,
          titleColor: '#f4eafe',
          bodyColor: '#f4eafe',
          callbacks: {
            title: items => {
              const item = items[0];
              return formatDate(item.dataset.dates?.[item.dataIndex] || item.label);
            },
            label: context => {
              const suffix = context.dataset.suffix || '';
              const unit = context.dataset.showCurrency && currency ? ` ${currency}` : '';
              return `${context.dataset.label}: ${formateNumber(context.raw)}${suffix}${unit}`;
            },
            afterLabel: context => {
              if (context.dataset.isPrevious) return '';
              const previous = Number(context.dataset.comparison?.[context.dataIndex]);
              const current = Number(context.raw);
              if (!Number.isFinite(previous) || previous === 0) return '';
              const percent = ((current / previous - 1) * 100).toFixed(2);
              return `${Number(percent) > 0 ? '+' : ''}${percent}% ${locale.toPreviousPeriod}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: 'rgba(255, 255, 255, 0.14)', borderColor: 'rgba(255, 255, 255, 0.3)' },
          ticks: {
            color: '#927f9b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: isMobile ? 8 : 10,
            callback: value => (isMobile ? chartData.axisLabels[value] : chartData.labels[value]),
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(255, 255, 255, 0.14)', borderColor: 'rgba(255, 255, 255, 0.3)' },
          ticks: { color: '#927f9b' },
        },
      },
    }),
    [chartData.axisLabels, chartData.datasets.length, chartData.labels, currency, isMobile, locale.toPreviousPeriod],
  );

  if (!chartData.datasets.length || !chartData.labels.length) {
    return <div className="report-chart__empty">{locale.noDataForSelectedPeriod}</div>;
  }

  return (
    <div className="report-chart">
      <Chart type="line" data={chartData} options={options} />
    </div>
  );
};

export default ReportChart;
